import React, { useEffect, useState } from 'react';
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router';
import { LeadFormActions } from '../../../Services/_actions';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DateRangeFilter from '../../Shared/DateRangeFilter/DateRangeFilter.tsx';
import TemplateDropdown from '../../Shared/TemplateDropdown/TemplateDropdown.tsx';
import RoleDropdown from '../../Shared/RoleDropdown/index.tsx';

const timeUnits = ['seconds', 'minutes', 'hours', 'days', 'months'];

const actionTypes = [
  { value: 'update_status', label: 'Update Status' },
  { value: 'send_lead_summary', label: 'Report' },
];

const reportTimeUnits = [
  // { value: 'second',  label: 'Seconds' },
  { value: 'minute', label: 'Minutes' },
  { value: 'hour', label: 'Hours' },
  { value: 'day', label: 'Days' },
  { value: 'week', label: 'Weeks' },
  { value: 'month', label: 'Months' },
];

const composeTriggerTime = (val?: number | string, unit?: string) => {
  const n = Number(val);
  if (!n || !unit) return '';
  // singular for 1, plural otherwise
  const u = n === 1 ? unit : `${unit}s`;
  return `${n} ${u}`;
};

const frequencyOptions = [
  { value: 'One-time', label: 'One-time' },
  { value: 'Hourly', label: 'Hourly' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
];

const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

interface MoveToStatusSchedularManagerProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const MoveToStatusSchedularManager: React.FC<MoveToStatusSchedularManagerProps> = ({
  formData,
  setFormData,
}) => {
  const { fetchStatusAndSubStatus } = LeadFormActions();
  const { showSnackBar } = useSnackBar();
  const { leadFormId } = useParams();
  const [nextStatusList, setNextStatusList] = useState<any[]>([]);
  const [dateFormData, setDateFormData] = useState<any>({});
  const [typeFilter, setTypeFilter] = useState<{ label: string; value: string }>({
    label: 'All',
    value: '',
  });
  const [fullStatusList, setFullStatusList] = useState<any[]>([]);

  const handleChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Reset fields that don't belong to the selected action
  const handleActionChange = (val: any) => {
    const action = val?.value ?? '';
    setFormData((prev) => ({
      ...prev,
      action,

      // always keep currentStatus & active as they are shared
      // wipe fields irrelevant to the chosen action:
      ...(action === 'update_status'
        ? {
            // keep status flow fields, clear message fields
            content: '',
            report: null,
            stakeholders: [],
            frequency: '',
            sendTime: '',
            sendDay: '',
            dateRange: { startDate: null, endDate: null },
            formDetails: null,
          }
        : {
            // keep message fields, clear status flow fields
            trigger_delay: '',
            trigger_delay_unit: '',
            nextStatus: null,
            description: '',
            //   reportIntervalValue: prev?.reportIntervalValue ?? 1,
            // reportIntervalUnit: prev?.reportIntervalUnit ?? 'day',
            // frequency: composeTriggerTime(prev?.reportIntervalValue ?? 1, prev?.reportIntervalUnit ?? 'day'),
          }),
    }));
  };

useEffect(() => {
  const fetchStatusList = async () => {
    try {
      const res = await fetchStatusAndSubStatus({ leadFormId, is_active: true });
      if (res?.status === 200) {
        setFullStatusList(res?.data || []);
        setNextStatusList(res?.data || []);
      }
    } catch {
      showSnackBar('Failed to load status list.', 'error');
    }
  };
  fetchStatusList();
}, [leadFormId]);

  useEffect(() => {
    const data = localStorage.getItem('leadWebFormData');
    setDateFormData(data ? JSON.parse(data) : {});
  }, []);

  const LeadWebForm = dateFormData?.webForm?.leads_form_items || [];
  const LeadWebFormArray = Object.values(LeadWebForm || {});

  const action = formData?.action;

  const getTypeOptionsFromStatusList = () => {
  const uniqueTypes = Array.from(
    new Set(fullStatusList.map((s) => s.type_of_status).filter(Boolean))
  );

  return [
    { label: 'All', value: 'all' },
    ...uniqueTypes.map((type) => ({ label: type, value: type })),
  ];
};

const handleSelectType = (selectedOption: any) => {
  setTypeFilter(selectedOption); 
  const typeValue = selectedOption?.value;

  if (typeValue && typeValue !== 'all') {
    const filteredList = fullStatusList.filter(
      (status) => status.type_of_status === typeValue
    );
    setNextStatusList(filteredList);
  } else {
    // Show all if "All" selected
    setNextStatusList(fullStatusList);
  }
};

  return (
    <Box sx={{ p: 2 }}>
      {/* Select Action (always visible) */}
      <SearchableDropdown
        options={actionTypes}
        label="Select Action"
        value={actionTypes.find((type) => type.value === action) ?? null}
        onChange={handleActionChange}
        sx={{ mb: 1 }}
      />

      {/* ------------------ UPDATE STATUS flow ------------------ */}
      {action === 'update_status' && (
        <>
          <SearchableDropdown
            options={getTypeOptionsFromStatusList()}
            value={typeFilter}
            onChange={handleSelectType}
            label="Type of Status"
            sx={{ mb: 1 }}
          />
          <SearchableDropdown
            options={nextStatusList}
            labelKey="status_name"
            valueKey="status_id"
            label="Select Current Status"
            value={formData?.currentStatus ?? null}
            onChange={(val: any) => {
              if (val) {
                handleChange('currentStatus', {
                  status_id: val?.status_id,
                  status_name: val?.status_name,
                });
              } else {
                handleChange('currentStatus', null);
              }
            }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            type="number"
            size="small"
            label="Trigger After"
            value={formData?.trigger_delay ?? ''}
            onChange={(e) => handleChange('trigger_delay', e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            select
            fullWidth
            size="small"
            label="Time Unit"
            value={formData?.trigger_delay_unit ?? ''}
            onChange={(e) => handleChange('trigger_delay_unit', e.target.value)}
            sx={{ mb: 2 }}
          >
            {timeUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit.charAt(0).toUpperCase() + unit.slice(1)}
              </MenuItem>
            ))}
          </TextField>

          <SearchableDropdown
            options={nextStatusList}
            labelKey="status_name"
            valueKey="status_id"
            label="Select Next Status"
            value={formData?.nextStatus ?? null}
            onChange={(val: any) =>
              handleChange(
                'nextStatus',
                val ? { status_id: val.status_id, status_name: val.status_name } : null
              )
            }
            getOptionDisabled={(option) => option.status_id === formData?.currentStatus?.status_id}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Description"
            multiline
            minRows={2}
            maxRows={4}
            value={formData?.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                sx={{ ml: 1 }}
                checked={formData?.active || false}
                onChange={(e) => handleChange('active', e.target.checked)}
                name="Active"
              />
            }
            label="Active"
          />
        </>
      )}

      {/* Divider between flows (optional) */}
      {action && <Divider sx={{ my: 1 }} />}

      {/* ------------------ SEND MESSAGE flow ------------------ */}
      {action === 'send_lead_summary' && (
        <>
          {/* <SearchableDropdown
            options={LeadWebFormArray}
            labelKey="alias_name"
            valueKey="key_name" 
            label="Select Form Details"
            value={formData?.formDetails ?? null}
            onChange={(val: any) => {
              if (val) {
                handleChange('formDetails', {
                  key_name: val?.key_name,
                  alias_name: val?.alias_name,
                  key_type: val?.key_type,
                });
              } else {
                handleChange('formDetails', null);
              }
            }}
            sx={{ mb: 1}}
          /> */}

          {/* "Select Current Status" already shown above (shared) */}

          {/* <TextField
            fullWidth
            size="small"
            label="Content"
            multiline
            minRows={2}
            maxRows={4}
            value={formData?.content || ''}
            onChange={(e) => handleChange('content', e.target.value)}
            sx={{ mb: 1 }}
          /> */}

          <TemplateDropdown
            value={formData?.template ?? null}
            onChange={(val: any) => handleChange('template', val)}
            label="Select Template"
            error={false}
            disabled={false}
            sx={{ mb: 1 }}
            dropdownFilters={{
              organisationId: formData?.organisation?.organisation_id ?? '',
              botNumber: formData?.botNumber ?? '',
            }}
          />

          {/* <RoleDropdown
            value={formData?.stakeholders ?? []}
            onChange={(val) => {
              const selectedUsers =
                val?.map((user: any) => ({ id: user?.id, name: user?.name })) ?? [];
              handleChange('stakeholders', selectedUsers);
            }}
            organisationId={formData?.organisation?.organisation_id ?? ''}
            multiSelect={true}
            label="Select Stakeholders"
            sx={{ mb: 1 }}
          /> */}

          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              type="number"
              size="small"
              label="Every"
              inputProps={{ min: 1 }}
              value={formData?.reportIntervalValue ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                // keep raw parts
                handleChange('reportIntervalValue', v);
                // also compose and store API-ready string in frequency
                handleChange('frequency', composeTriggerTime(v, formData?.reportIntervalUnit));
              }}
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Unit"
              value={formData?.reportIntervalUnit ?? ''}
              onChange={(e) => {
                const u = e.target.value;
                handleChange('reportIntervalUnit', u);
                handleChange('frequency', composeTriggerTime(formData?.reportIntervalValue, u));
                // clear sendDay if unit is not week/month
                if (!['week', 'month'].includes(u)) handleChange('sendDay', null);
              }}
            >
              {reportTimeUnits.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {u.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Send Time
          </Typography> */}
          <TextField
            fullWidth
            type="time"
            size="small"
            label="Select Time"
            value={formData?.sendTime ?? ''}
            onChange={(e) => handleChange('sendTime', e.target.value)}
            sx={{ mb: 1 }}
          />

          {['Weekly', 'Monthly'].includes(formData?.reportIntervalUnit) && (
            <SearchableDropdown
              options={daysOfWeek}
              label="Select Day"
              value={daysOfWeek.find((d) => d.value === formData?.sendDay) ?? null}
              onChange={(val: any) => handleChange('sendDay', val?.value)}
              sx={{ mb: 1 }}
            />
          )}

          {/* <DateRangeFilter
            startDate={formData?.dateRange?.startDate ?? null}
            endDate={formData?.dateRange?.endDate ?? null}
            onApply={(range) => handleChange('dateRange', range)}
          /> */}

          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                sx={{ ml: 1 }}
                checked={formData?.active || false}
                onChange={(e) => handleChange('active', e.target.checked)}
                name="Active"
              />
            }
            label="Active"
          />
        </>
      )}
    </Box>
  );
};

export default MoveToStatusSchedularManager;
